import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../../config';
import { tvUploadRequest } from '../../services/tvUploadRequest';
import { loadYouTubeApi } from '../../services/youtubePlayerApi';
import '../../styles/youtube-programme.css';

export default function YouTubeChannel() {
    const navigate = useNavigate();
    const [videos, setVideos] = useState([]);
    const [index, setIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [blocked, setBlocked] = useState(false);
    const [ready, setReady] = useState(false);
    const [showProgramme, setShowProgramme] = useState(false);
    const [reload, setReload] = useState(0);
    const container = useRef(null);
    const mount = useRef(null);
    const player = useRef(null);
    const activeVideo = useRef(null);
    const advance = useRef(() => {});
    const failed = useRef(new Set());
    const current = videos[index];
    activeVideo.current = current;
    advance.current = () => {
        if (!videos.length) return;
        let next = index;
        for (let count = 0; count < videos.length; count++) {
            next = (next + 1) % videos.length;
            if (!failed.current.has(videos[next].id)) {
                if (next === index) player.current?.loadVideoById(videos[next].video_id);
                else setIndex(next);
                return;
            }
        }
        setError('Aucune vidéo du programme ne peut être lue actuellement.');
    };
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true); setError(''); failed.current.clear();
        tvUploadRequest(`${config.apiBase}/youtube_programme.php`, { signal: controller.signal })
            .then(data => { if (!controller.signal.aborted) { setVideos(data.videos || []); setIndex(0); } })
            .catch(e => { if (!controller.signal.aborted) setError(e.message); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [reload]);
    useEffect(() => {
        if (!videos.length) return undefined;
        let disposed = false;
        setReady(false);
        const node = document.createElement('div');
        mount.current.replaceChildren(node);
        loadYouTubeApi().then(YT => {
            if (disposed) return;
            player.current = new YT.Player(node, {
                width: '100%', height: '100%',
                playerVars: { playsinline: 1, controls: 1, rel: 0, fs: 1, origin: window.location.origin },
                events: {
                    onReady: event => {
                        if (disposed) return;
                        const frame = event.target.getIframe();
                        frame.title = 'Programme YouTube IPTIC Galkoye';
                        frame.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
                        frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
                        setReady(true);
                    },
                    onStateChange: event => { if (!disposed && event.data === 0) advance.current(); },
                    onAutoplayBlocked: () => { if (!disposed) setBlocked(true); },
                    onError: event => {
                        if (disposed) return;
                        if (![100, 101, 150].includes(event.data)) { setError('Lecture YouTube indisponible. Réessayez ou ouvrez la vidéo sur YouTube.'); return; }
                        if (activeVideo.current) failed.current.add(activeVideo.current.id);
                        advance.current();
                    }
                }
            });
        }).catch(e => { if (!disposed) setError(e.message); });
        return () => { disposed = true; player.current?.destroy(); player.current = null; };
    }, [videos]);
    useEffect(() => {
        if (ready && current) { setError(''); setBlocked(false); player.current?.loadVideoById(current.video_id); }
    }, [ready, current]);
    const fullscreen = async () => {
        try {
            if (document.fullscreenElement) await document.exitFullscreen();
            else if (container.current.requestFullscreen) await container.current.requestFullscreen();
            else setError('Utilisez le bouton plein écran du lecteur YouTube sur cet appareil.');
        } catch { setError('Utilisez le bouton plein écran du lecteur YouTube sur cet appareil.'); }
    };
    return <main ref={container} className="youtube-channel">
        <header className="youtube-channel-bar"><button onClick={() => navigate('/etudiant')}>← Retour</button><div><span className="youtube-eyebrow">IPTIC Galkoye</span><h1>YouTube · Votre programme</h1></div><button onClick={fullscreen}>Plein écran</button></header>
        <div className="youtube-channel-stage"><div ref={mount} className="youtube-player-mount" />
            {loading && <div className="youtube-stage-message" role="status">Chargement du programme…</div>}
            {!loading && videos.length > 0 && !ready && !error && <div className="youtube-stage-message" role="status">Initialisation du lecteur YouTube…</div>}
            {!loading && !videos.length && !error && <div className="youtube-stage-message">Le technicien n’a pas encore publié de vidéo YouTube.</div>}
        </div>
        <footer className="youtube-channel-controls"><div><span className="youtube-eyebrow">À l’antenne {videos.length ? `· ${index + 1} / ${videos.length}` : ''}</span><h2>{current?.titre || 'Programme YouTube'}</h2></div><div className="youtube-channel-actions"><button disabled={!ready || !videos.length} onClick={() => { setBlocked(false); player.current?.playVideo(); }}>Lire</button><button disabled={!ready || !videos.length} onClick={() => advance.current()}>Suivante →</button><button onClick={() => setShowProgramme(value => !value)} aria-expanded={showProgramme}>Programme</button></div>
            {blocked && <p role="status">Appuyez sur « Lire » pour démarrer la vidéo.</p>}
            {error && <p role="alert">{error} <button onClick={() => setReload(value => value + 1)}>Réessayer</button>{current && <> <a href={`https://www.youtube.com/watch?v=${current.video_id}`} target="_blank" rel="noopener noreferrer">Ouvrir sur YouTube</a></>}</p>}
        </footer>
        {showProgramme && <aside className="youtube-programme-list" aria-label="Programme"><p>Les journées récentes d’abord, avec votre sélection personnelle dans chaque journée.</p>{videos.map((video, position) => <button key={video.id} aria-current={position === index ? 'true' : undefined} onClick={() => { if (position === index) player.current?.playVideo(); else setIndex(position); setShowProgramme(false); }}><img src={`https://i.ytimg.com/vi/${video.video_id}/mqdefault.jpg`} alt="" loading="lazy" /><span>{video.titre}</span></button>)}</aside>}
    </main>;
}
